// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Bootstrap: the screens, the clock, and every tap.
//
// ── THE CLOCK IS A setInterval, NOT rAF ─────────────────────────────────────────────────────────
// `requestAnimationFrame` is a RENDER signal and this file treats it as nothing else. A browser stops
// firing it the moment a tab is backgrounded, so a tower driven by rAF would simply stop when the
// player looked at something else and then jump. The simulation is advanced by a 100ms interval with
// a bounded catch-up; rAF only ever paints what the simulation already decided.

import '@ben-gy/game-engine/mobile.css';
import './styles/main.css';
import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { newSeed } from '@ben-gy/game-engine/rng';

import { CUES, PATCHES } from './cues';
import { createLog } from './events';
import { canvasHeightFor, cellAt, draw, layout, onSpine, plotStoreys, riserUnder, spineStoreyAt, unitUnder, type Layout, type Overlay } from './render';
import { Sim } from './sim';
import { MODES, modeOf, type ModeId } from './modes';
import { DAY_TICKS, DEFAULT_SPEED, SPEEDS, STOREYS, UNITS } from './tuning';
import * as ui from './ui';
import { createRun, createTutorialSim, STEPS, type Allow, type TutorialRun } from './tutorial';

const store = createStore('riser');
const sfx = createSfx({ muted: store.get('muted', false), patches: PATCHES });
// A cue with no patch behind it plays SILENTLY — `sfx.play` does not throw and does not warn — so a
// typo is an inaudible bug that leaves the whole suite green. The roster is resolved once at boot and
// anything unknown degrades to a sound that definitely exists, rather than to nothing at all.
const KNOWN = new Set<string>(CUES.filter((c) => sfx.has(c)));
const ping = (cue: string, opts?: { pitch?: number }): void => {
  try {
    sfx.play(KNOWN.has(cue) ? cue : 'blip', opts);
  } catch {
    /* audio is a nicety, never a dependency */
  }
};

const root = document.getElementById('app');
if (!root) throw new Error('no mount');
const els = ui.build(root);
hardenViewport();

const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const ctx = els.canvas.getContext('2d');

let sim: Sim | null = null;
let mode: ModeId = modeOf(store.get('mode', 'ascent')).id;
let seed = 0;
let speed = DEFAULT_SPEED;
let overlay: Overlay = 'lift';
let tool: ui.ToolId | null = null;
let L: Layout = layout(320, 480, 1);
let tutorial: TutorialRun | null = null;
let flash = -1;
let flashUntil = 0;
let selected: number | null = null;
let ghostCell: { storey: number; col: number } | null = null;
const log = createLog(512);

// ── screens ─────────────────────────────────────────────────────────────────────────────────────

function show(which: 'menu' | 'game' | 'results'): void {
  els.menu.hidden = which !== 'menu';
  els.game.hidden = which !== 'game';
  els.results.hidden = which !== 'results';
  // The attribution footer is on every screen EXCEPT a live tower — nobody explores the site
  // mid-shift, and on a phone the backlink steals a strip of the play area.
  document.body.classList.toggle('playing', which === 'game');
}

ui.setMode(els, mode);
show('menu');

// ── the sheet, with all four ways out ───────────────────────────────────────────────────────────

let sheetOpener: HTMLElement | null = null;
let sheetArmed = 0;

function openSheet(html: string, opener?: HTMLElement | null): void {
  els.sheetBody.innerHTML = html;
  els.sheet.hidden = false;
  sheetOpener = opener ?? null;
  // Armed a beat late: the tap that OPENED this panel is still travelling, and a backdrop listener
  // added synchronously catches its own trailing click and closes again instantly.
  sheetArmed = performance.now() + 300;
  els.sheetClose.focus();
}

function closeSheet(): void {
  if (els.sheet.hidden) return;
  els.sheet.hidden = true;
  els.sheetBody.innerHTML = '';
  sheetOpener?.focus();
  sheetOpener = null;
}

els.sheetClose.addEventListener('click', closeSheet);
els.sheet.addEventListener('pointerdown', (e) => {
  if (performance.now() < sheetArmed) return;
  const t = e.target as HTMLElement;
  if (!t.closest('.panel')) closeSheet();
});
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') {
    if (!els.sheet.hidden) closeSheet();
    return;
  }
  if (els.game.hidden || !sim) return;
  if (e.key === ' ') {
    e.preventDefault();
    setSpeed(speed === 0 ? DEFAULT_SPEED : 0);
  } else if (e.key === '`') {
    setOverlay(overlay === 'lift' ? 'mood' : overlay === 'mood' ? 'money' : 'lift');
  } else if (e.key === 'Escape') {
    arm(null);
  } else if (/^[1-8]$/.test(e.key)) {
    const t = els.tools.querySelectorAll<HTMLElement>('.tool')[Number(e.key) - 1];
    if (t?.dataset.tool) arm(t.dataset.tool as ui.ToolId);
  }
});

// ── menu ────────────────────────────────────────────────────────────────────────────────────────

els.modeList.addEventListener('click', (e) => {
  const b = (e.target as HTMLElement).closest<HTMLElement>('.mode');
  if (!b?.dataset.mode) return;
  mode = modeOf(b.dataset.mode).id;
  store.set('mode', mode);
  ui.setMode(els, mode);
  ping('pick');
});

document.getElementById('play')?.addEventListener('click', () => startRun(mode, urlSeed() ?? newSeed()));
document.getElementById('howto')?.addEventListener('click', (e) => startTutorial(e.currentTarget as HTMLElement));
document.getElementById('about')?.addEventListener('click', (e) => openSheet(ui.ABOUT, e.currentTarget as HTMLElement));

const muteBtn = document.getElementById('mute') as HTMLButtonElement | null;
function paintMute(): void {
  if (!muteBtn) return;
  muteBtn.textContent = sfx.muted() ? 'Sound off' : 'Sound on';
  muteBtn.setAttribute('aria-pressed', String(sfx.muted()));
}
muteBtn?.addEventListener('click', () => {
  sfx.setMuted(!sfx.muted());
  store.set('muted', sfx.muted());
  paintMute();
});
paintMute();

// ── running a tower ─────────────────────────────────────────────────────────────────────────────

function startRun(m: ModeId, s: number): void {
  sfx.unlock();
  sim = new Sim(m, s, log.sink);
  seed = s;
  speed = DEFAULT_SPEED;
  tool = null;
  selected = null;
  tutorial = null;
  els.banner.hidden = true;
  ui.setTool(els, null);
  setOverlay('lift');
  show('game');
  resize();
  els.context.textContent = `${MODES[m].rule}`;
}

function startTutorial(opener?: HTMLElement | null): void {
  sfx.unlock();
  sim = createTutorialSim();
  seed = sim.seed;
  speed = 2;
  tool = null;
  tutorial = createRun();
  els.banner.hidden = false;
  show('game');
  resize();
  paintBanner();
  void opener;
}

els.bannerSkip.addEventListener('click', () => {
  tutorial?.skip();
  store.set('taught', true);
  els.banner.hidden = true;
  ping('step');
});

function paintBanner(): void {
  const step = tutorial?.step();
  if (!tutorial || !step) {
    els.banner.hidden = true;
    if (tutorial?.done()) {
      store.set('taught', true);
      els.context.textContent = 'That is the game. Keep it moving.';
    }
    return;
  }
  els.bannerText.textContent = `${tutorial.index + 1}/${STEPS.length} · ${step.copy}`;
}

function advance(kind: Allow): void {
  if (!tutorial) return;
  const before = tutorial.index;
  tutorial.did(kind);
  if (tutorial.index !== before) {
    ping('step');
    els.context.textContent = tutorial.lastAfter;
    paintBanner();
  }
}

const gated = (kind: Allow): boolean => !tutorial || tutorial.allows(kind);

document.getElementById('quit')?.addEventListener('click', () => {
  show('menu');
  sim = null;
});

els.speed.addEventListener('click', () => setSpeed((speed + 1) % SPEEDS.length || 1));
function setSpeed(v: number): void {
  speed = v;
  if (sim) ui.updateHud(els, sim, speed);
}

els.rail.addEventListener('click', (e) => {
  const b = (e.target as HTMLElement).closest<HTMLElement>('.pill');
  if (b?.dataset.ov) {
    setOverlay(b.dataset.ov as Overlay);
    ping('pick');
  }
});
function setOverlay(o: Overlay): void {
  overlay = o;
  ui.setOverlay(els, o);
}

els.tools.addEventListener('click', (e) => {
  const b = (e.target as HTMLElement).closest<HTMLElement>('.tool');
  if (!b?.dataset.tool) return;
  arm(b.dataset.tool as ui.ToolId);
});

function arm(t: ui.ToolId | null): void {
  if (sim && !sim.mode.build && t) {
    els.context.textContent = 'Nothing can be built in this mode — run the lifts you have.';
    ping('deny');
    return;
  }
  tool = tool === t ? null : t;
  ui.setTool(els, tool);
  els.context.textContent = tool ? ui.toolHint(tool) : 'Tap a crowd to send a car to it.';
  if (tool) ping('pick');
}

// ── the canvas: one pointer, three meanings, all decided by STATE ───────────────────────────────
//
// Which one a tap means is never inferred from how it moved. If a tool is armed the tap places;
// otherwise a tap on the spine points at a storey, a tap on a crowd summons, and a tap on anything
// else opens its card. There is no gesture in this game that has to be told apart from another one,
// because there is no camera to pan.

els.canvas.addEventListener('pointerdown', (e) => {
  if (!sim || els.game.hidden) return;
  els.canvas.setPointerCapture(e.pointerId);
  const r = els.canvas.getBoundingClientRect();
  const px = e.clientX - r.left;
  const py = e.clientY - r.top;
  if (tool) {
    const c = cellAt(L, px, py);
    ghostCell = c;
    if (c) els.context.textContent = ui.verdictFor(sim, tool, c.storey, c.col);
  }
});

els.canvas.addEventListener('pointermove', (e) => {
  if (!sim || !tool) return;
  const r = els.canvas.getBoundingClientRect();
  ghostCell = cellAt(L, e.clientX - r.left, e.clientY - r.top);
});

els.canvas.addEventListener('pointercancel', () => {
  ghostCell = null;
});

els.canvas.addEventListener('pointerup', (e) => {
  if (!sim || els.game.hidden) return;
  const r = els.canvas.getBoundingClientRect();
  const px = e.clientX - r.left;
  const py = e.clientY - r.top;
  const cell = cellAt(L, px, py);
  const armed = tool;
  ghostCell = null;

  if (armed) {
    if (cell) place(armed, cell.storey, cell.col);
    return;
  }
  if (onSpine(L, px)) {
    const s = spineStoreyAt(L, py);
    flash = s;
    flashUntil = performance.now() + 900;
    setOverlay('lift');
    ping('blip');
    els.context.textContent = describeStorey(s);
    advance('spine');
    return;
  }
  if (!cell) return;

  const pressure = sim.storeyPressure(cell.storey);
  if (pressure.n > 0 && gated('summon')) {
    const q = sim.worstQueueOn(cell.storey);
    if (q && sim.summon(q.riser, cell.storey, q.dir)) {
      flash = cell.storey;
      flashUntil = performance.now() + 600;
      ping('ding', { pitch: 1.4 });
      els.context.textContent = `Car on its way to storey ${cell.storey}.`;
      advance('summon');
      return;
    }
    ping('deny');
    els.context.textContent = 'A car is already almost there.';
    return;
  }

  const rid = riserUnder(sim, cell.storey, cell.col);
  if (rid >= 0) {
    openSheet(ui.riserCard(sim, rid));
    return;
  }
  const u = unitUnder(sim, cell.storey, cell.col);
  if (u) {
    selected = u.id;
    openSheet(ui.unitCard(sim, u.id));
  }
});

function describeStorey(s: number): string {
  if (!sim) return '';
  const p = sim.storeyPressure(s);
  if (p.n === 0) return `Storey ${s}: nobody waiting.`;
  return `Storey ${s}: ${p.n} waiting, longest ${(p.worstTicks / 10).toFixed(0)}s.`;
}

function place(t: ui.ToolId, storey: number, col: number): void {
  if (!sim) return;
  if (!gated(t === 'car' ? 'car' : 'build')) {
    ping('deny');
    return;
  }
  let ok = false;
  if (t === 'sell') {
    const u = unitUnder(sim, storey, col);
    ok = !!u && sim.sellUnit(u);
    if (ok) ping('raze');
  } else if (t === 'car') {
    const rid = riserUnder(sim, storey, col);
    const r = sim.tower.risers.find((x) => x.id === rid);
    if (r) {
      const v = sim.canBuyCar(r, false);
      ok = v.ok && !!sim.buyCar(r, false);
      if (!v.ok) els.context.textContent = v.why;
    } else els.context.textContent = 'Tap a riser to put a car in it.';
    if (ok) {
      ping('mend');
      advance('car');
    }
  } else if (t === 'riser') {
    const hi = Math.max(8, storey);
    const v = sim.canBuildRiser(hi, col);
    ok = v.ok && !!sim.buildRiser(hi, col);
    if (!v.ok) els.context.textContent = v.why;
    if (ok) ping('build');
  } else if (t === 'stair') {
    const v = sim.canBuildStair(storey, col);
    ok = v.ok && sim.buildStair(storey, col);
    if (!v.ok) els.context.textContent = v.why;
    if (ok) ping('build');
  } else {
    const v = sim.canBuildUnit(t, storey, col);
    ok = v.ok && !!sim.buildUnit(t, storey, col);
    if (!v.ok) els.context.textContent = v.why;
    if (ok) {
      ping('build', { pitch: 1 + storey * 0.02 });
      advance('build');
    }
  }
  if (!ok) {
    ping('deny');
    return;
  }
  els.context.textContent = `${t === 'sell' ? 'Sold' : 'Built'}. £${Math.round(sim.cash).toLocaleString('en-GB')} left.`;
}

// riser card buttons live inside the sheet, so they are delegated
els.sheetBody.addEventListener('click', (e) => {
  if (!sim) return;
  const b = (e.target as HTMLElement).closest<HTMLElement>('button');
  const row = b?.closest<HTMLElement>('[data-riser]');
  if (!b || !row) return;
  const r = sim.tower.risers.find((x) => x.id === Number(row.dataset.riser));
  if (!r) return;
  if (b.dataset.policy && gated('policy')) {
    sim.setPolicy(r, b.dataset.policy as 'all' | 'express' | 'down', r.expressFrom);
    ping('pick');
    advance('policy');
    els.sheetBody.innerHTML = ui.riserCard(sim, r.id);
  } else if (b.dataset.extend) {
    const hi = Math.min(STOREYS - 1, r.hi + Number(b.dataset.extend));
    const v = sim.canExtend(r, hi);
    if (v.ok && sim.extend(r, hi)) {
      ping('build');
      els.sheetBody.innerHTML = ui.riserCard(sim, r.id);
    } else ping('deny');
  } else if (b.dataset.addcar && gated('car')) {
    if (sim.canBuyCar(r, false).ok && sim.buyCar(r, false)) {
      ping('mend');
      advance('car');
      els.sheetBody.innerHTML = ui.riserCard(sim, r.id);
    } else ping('deny');
  } else ping('deny');
});

// ── the clock ───────────────────────────────────────────────────────────────────────────────────

let acc = 0;
let lastTick = performance.now();
setInterval(() => {
  const now = performance.now();
  const dt = Math.min(1000, now - lastTick);
  lastTick = now;
  if (!sim || sim.over || els.game.hidden) return;
  const rate = SPEEDS[speed];
  if (rate === 0) return;
  acc += (dt / 1000) * rate;
  // A full second of catch-up, because a backgrounded tab clamps timers to roughly 1Hz and a smaller
  // budget would quietly run the tower at a fraction of its speed rather than pausing honestly.
  let budget = Math.min(acc, rate);
  while (budget >= 1 && !sim.over) {
    sim.step();
    acc -= 1;
    budget -= 1;
  }
  drainEvents();
  // The HUD is repainted from the CLOCK, not from the render loop. rAF does not fire in a backgrounded
  // tab, so a game whose numbers are only written inside `frame()` shows a player stale cash and a
  // stale day for as long as they are looking at something else — and then jumps. rAF paints the
  // tower; the interval owns the truth.
  ui.updateHud(els, sim, speed);
  ui.updateRail(els, sim);
  if (sim.over) finish();
}, 100);

function drainEvents(): void {
  for (const e of log.drain()) {
    if (e.k === 'notice') ping('notice');
    else if (e.k === 'let') ping('lease');
    else if (e.k === 'day') ping('daybreak');
  }
}

function finish(): void {
  if (!sim) return;
  ping(sim.won ? 'award' : 'bust');
  const best = store.get<number>(`best:${sim.mode.id}`, 0);
  if (sim.rating > best) store.set(`best:${sim.mode.id}`, sim.rating);
  els.resultsBody.innerHTML = ui.results(sim);
  show('results');
  document.getElementById('again')?.addEventListener('click', () => startRun(mode, newSeed()));
  document.getElementById('toMenu')?.addEventListener('click', () => show('menu'));
  document.getElementById('share')?.addEventListener('click', share);
  els.resultsBody.querySelector('.results-feedback')?.addEventListener('click', (e) => {
    window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement });
  });
}

async function share(): Promise<void> {
  if (!sim) return;
  const url = `${location.origin}${location.pathname}?mode=${sim.mode.id}&seed=${seed}`;
  const text = `Riser · ${sim.mode.name}: rating ${sim.rating}, ${sim.population()} people, ${Math.round(sim.stats.personSecondsLost / 3600)} person-hours queued. Same tower, same traffic:`;
  try {
    if (navigator.share) await navigator.share({ title: 'Riser', text, url });
    else {
      await navigator.clipboard.writeText(`${text} ${url}`);
      els.resultsBody.querySelector('#share')?.setAttribute('data-copied', 'yes');
    }
  } catch {
    /* the player cancelled, or the browser refused — never a failure worth shouting about */
  }
}

function urlSeed(): number | null {
  const p = new URLSearchParams(location.search);
  const s = Number(p.get('seed'));
  const m = p.get('mode');
  if (m) {
    mode = modeOf(m).id;
    ui.setMode(els, mode);
  }
  return Number.isFinite(s) && s > 0 ? s : null;
}

// ── painting ────────────────────────────────────────────────────────────────────────────────────

function resize(): void {
  const r = els.stage.getBoundingClientRect();
  // A transient 0-size measurement yields NaN world coordinates and silently drops every tap, so it
  // is ignored and retried rather than used.
  if (r.width < 8 || r.height < 8) {
    setTimeout(resize, 60);
    return;
  }
  const dpr = Math.min(3, window.devicePixelRatio || 1);
  const storeys = plotStoreys(sim ? sim.tower.storeys : 10);
  const h = canvasHeightFor(r.height, storeys);
  els.canvas.width = Math.round(r.width * dpr);
  els.canvas.height = Math.round(h * dpr);
  els.canvas.style.width = `${r.width}px`;
  els.canvas.style.height = `${h}px`;
  els.stage.classList.toggle('scrolls', h > r.height + 1);
  L = layout(r.width, h, dpr, storeys);
}
window.addEventListener('resize', resize);
window.addEventListener('orientationchange', () => setTimeout(resize, 120));

let plotFor = -1;
function frame(): void {
  requestAnimationFrame(frame);
  if (!ctx || !sim || els.game.hidden) return;
  // The plot grows with the building, so a storey added at midnight has to re-fit the canvas.
  if (plotStoreys(sim.tower.storeys) !== plotFor) {
    plotFor = plotStoreys(sim.tower.storeys);
    resize();
  }
  const now = performance.now();
  const legal =
    !!ghostCell && !!tool && ui.verdictFor(sim, tool, ghostCell.storey, ghostCell.col).startsWith(name0(tool));
  draw(ctx, sim, L, {
    overlay,
    ghost: ghostCell && tool ? { kind: toolGhost(tool), storey: ghostCell.storey, col: ghostCell.col, legal } : null,
    flash,
    flashUntil,
    now,
    reduced,
    selected,
  });
}

const name0 = (t: ui.ToolId): string =>
  t === 'riser' ? 'Cut' : t === 'stair' ? 'Stairs' : t === 'car' || t === 'sell' ? 'Tap' : UNITS[t].name;

const toolGhost = (t: ui.ToolId): 'office' | 'flat' | 'shop' | 'service' | 'riser' | 'stair' | null =>
  t === 'car' || t === 'sell' ? null : t;

requestAnimationFrame(frame);

// A player who has never seen this game gets taught before they get a menu.
if (!store.get('taught', false)) startTutorial();
void DAY_TICKS;
